import type { QueryClient } from '@tanstack/react-query';
import { collectionItemsKey, collectionKeys, type Collection } from './entity';

export function setCollectionCache(qc: QueryClient, workspaceId: number, c: Collection): void {
  qc.setQueryData(collectionKeys.detail(workspaceId, c.id), c);
}

export function getCollectionCache(qc: QueryClient, workspaceId: number, id: string): Collection | undefined {
  return qc.getQueryData<Collection>(collectionKeys.detail(workspaceId, id));
}

export function removeCollectionCache(qc: QueryClient, workspaceId: number, id: string): void {
  qc.removeQueries({ queryKey: collectionKeys.detail(workspaceId, id) });
}

export function invalidateCollectionLists(qc: QueryClient, workspaceId: number): void {
  void qc.invalidateQueries({ queryKey: collectionKeys.lists(workspaceId) });
}

/**
 * An import rewrites the rows server-side, which no mutation of ours observes — the polls
 * watch `importing` and invalidate from there. The prefix covers every page and filter
 * variant for the collection.
 */
export function invalidateCollectionItems(qc: QueryClient, workspaceId: number, id: string): void {
  void qc.invalidateQueries({ queryKey: collectionItemsKey(workspaceId, id) });
}

/**
 * The list poll and the detail poll both carry `importing`, so either can be the one to see it
 * clear — and whichever writes the detail cache first destroys the evidence for the other. Both
 * therefore land their responses here, where the transition is read before it is overwritten.
 */
export function setFetchedCollectionCache(qc: QueryClient, workspaceId: number, fetched: Collection): void {
  const wasImporting = getCollectionCache(qc, workspaceId, fetched.id)?.importing === true;

  setCollectionCache(qc, workspaceId, fetched);

  if (wasImporting && !fetched.importing) {
    invalidateCollectionItems(qc, workspaceId, fetched.id);
  }
}

export function setUpdatedCollectionCache(qc: QueryClient, workspaceId: number, updated: Collection): void {
  // Keep the server-derived fields an edit can't change, in case the response omits them.
  const merge = (prev: Collection | undefined): Collection =>
    prev == null ? updated : { ...updated, in_use: prev.in_use, rows: prev.rows, bytes: prev.bytes };

  qc.setQueryData<Collection>(collectionKeys.detail(workspaceId, updated.id), merge);

  qc.setQueriesData<Collection[]>({ queryKey: collectionKeys.lists(workspaceId) }, (rows) =>
    rows?.map((row) => (row.id === updated.id ? merge(row) : row)),
  );
}
